// Enums e regras do domínio. Fonte única para API, web e CLI/MCP.

export const TASK_STATUSES = ['backlog', 'todo', 'in_progress', 'in_review', 'blocked', 'done', 'canceled'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const OPEN_STATUSES: readonly TaskStatus[] = ['backlog', 'todo', 'in_progress', 'in_review', 'blocked'];
export const CLOSED_STATUSES: readonly TaskStatus[] = ['done', 'canceled'];

/** Filtro de cards arquivados nas consultas. */
export const TASK_ARCHIVED_FILTERS = ['exclude', 'include', 'only'] as const;
/** Datas que o filtro de período (from/to) pode usar. */
export const TASK_DATE_FIELDS = ['created', 'updated', 'closed', 'due', 'archived'] as const;
export const TASK_DATE_LABELS: Record<(typeof TASK_DATE_FIELDS)[number], string> = {
  created: 'Criação',
  updated: 'Atualização',
  closed: 'Conclusão/cancelamento',
  due: 'Prazo',
  archived: 'Arquivamento',
};

export const STATUS_LABELS: Record<TaskStatus, string> = {
  backlog: 'Backlog',
  todo: 'A Fazer',
  in_progress: 'Em Progresso',
  in_review: 'Em Revisão',
  blocked: 'Bloqueado',
  done: 'Concluído',
  canceled: 'Cancelado',
};

export const TASK_TYPES = ['epic', 'story', 'feature', 'bug', 'refactor', 'task', 'research', 'documentation', 'infrastructure', 'security'] as const;
export type TaskType = (typeof TASK_TYPES)[number];

export const TYPE_LABELS: Record<TaskType, string> = {
  epic: 'Épico',
  story: 'História',
  feature: 'Feature',
  bug: 'Bug',
  refactor: 'Refactor',
  task: 'Tarefa',
  research: 'Pesquisa',
  documentation: 'Documentação',
  infrastructure: 'Infraestrutura',
  security: 'Segurança',
};

export const PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
};

export const PRIORITY_WEIGHT: Record<Priority, number> = { low: 1, medium: 2, high: 3, critical: 4 };

export const PROJECT_STATUSES = ['planning', 'active', 'paused', 'maintenance', 'archived'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  planning: 'Planejamento',
  active: 'Ativo',
  paused: 'Pausado',
  maintenance: 'Manutenção',
  archived: 'Arquivado',
};

/** Relações gravadas. "Bloqueada por" é o inverso de `blocks`; "subtarefa de" usa `tasks.parentKey`. */
export const RELATION_TYPES = ['blocks', 'relates_to', 'duplicates'] as const;
export type RelationType = (typeof RELATION_TYPES)[number];

/** Relações aceitas na API (inclui inversos e subtarefa). */
export const LINK_TYPES = ['blocks', 'blocked_by', 'relates_to', 'duplicates', 'duplicated_by', 'subtask_of'] as const;
export type LinkType = (typeof LINK_TYPES)[number];

export const LINK_TYPE_LABELS: Record<LinkType, string> = {
  blocks: 'Bloqueia',
  blocked_by: 'Bloqueada por',
  relates_to: 'Relacionada a',
  duplicates: 'Duplicada de',
  duplicated_by: 'Tem duplicata',
  subtask_of: 'Subtarefa de',
};

export const REF_KINDS = ['doc', 'pull_request', 'commit', 'file', 'issue', 'design', 'other'] as const;
export type RefKind = (typeof REF_KINDS)[number];

export const ACTOR_KINDS = ['user', 'agent', 'integration', 'system'] as const;
export type ActorKind = (typeof ACTOR_KINDS)[number];

export const USER_ROLES = ['admin', 'member'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const CRITERION_STATUSES = ['pending', 'met', 'not_met', 'waived'] as const;
export type CriterionStatus = (typeof CRITERION_STATUSES)[number];

export const VALIDATION_KINDS = ['unit', 'integration', 'e2e', 'typecheck', 'lint', 'build', 'manual', 'other'] as const;
export type ValidationKind = (typeof VALIDATION_KINDS)[number];

export const VALIDATION_STATUSES = ['passed', 'failed', 'skipped', 'error'] as const;
export type ValidationStatus = (typeof VALIDATION_STATUSES)[number];

export const EXECUTION_STATUSES = ['running', 'succeeded', 'partial', 'failed', 'aborted'] as const;
export type ExecutionStatus = (typeof EXECUTION_STATUSES)[number];

export const COMMENT_KINDS = ['comment', 'discovery', 'decision', 'note'] as const;
export type CommentKind = (typeof COMMENT_KINDS)[number];

/** Escopos dos tokens de agente. */
export const AGENT_SCOPES = [
  'projects:read',
  'projects:write',
  'tasks:read',
  'tasks:write',
  'labels:write',
  'executions:write',
  /** Concluir tarefas (done). Só um administrador concede; fica fora dos escopos padrão. */
  'tasks:complete',
] as const;
export type AgentScope = (typeof AGENT_SCOPES)[number];

export const DEFAULT_AGENT_SCOPES: AgentScope[] = ['projects:read', 'tasks:read', 'tasks:write', 'labels:write', 'executions:write'];

/** Transições permitidas (de → para). */
export const TRANSITIONS: Record<TaskStatus, readonly TaskStatus[]> = {
  backlog: ['todo', 'in_progress', 'blocked', 'canceled'],
  todo: ['backlog', 'in_progress', 'blocked', 'canceled'],
  in_progress: ['todo', 'in_review', 'blocked', 'done', 'canceled'],
  in_review: ['in_progress', 'done', 'blocked', 'canceled'],
  blocked: ['backlog', 'todo', 'in_progress', 'in_review', 'canceled'],
  done: ['in_progress', 'todo'],
  canceled: ['backlog', 'todo'],
};

export function canTransition(from: TaskStatus, to: TaskStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

/** Transições que exigem motivo. */
export function transitionNeedsReason(from: TaskStatus, to: TaskStatus): boolean {
  return to === 'blocked' || to === 'canceled' || from === 'done' || from === 'canceled';
}

export function isOpenStatus(s: TaskStatus): boolean {
  return OPEN_STATUSES.includes(s);
}

export const TASK_KEY_PREFIX = 'JYN';
export const TASK_KEY_RE = /^JYN-\d+$/;
export const PROJECT_KEY_RE = /^[a-z0-9][a-z0-9-]{1,39}$/;

/** Etiquetas iniciais (globais). */
export const DEFAULT_LABELS: { name: string; color: string; description: string }[] = [
  { name: 'bug', color: '#dc2626', description: 'Comportamento incorreto' },
  { name: 'feature', color: '#2563eb', description: 'Funcionalidade nova' },
  { name: 'refactor', color: '#7c3aed', description: 'Reestruturação sem mudar comportamento' },
  { name: 'technical-debt', color: '#a16207', description: 'Dívida técnica' },
  { name: 'infrastructure', color: '#0f766e', description: 'Infraestrutura, deploy, CI' },
  { name: 'backend', color: '#1e3a8a', description: 'API e serviços' },
  { name: 'frontend', color: '#0891b2', description: 'Web' },
  { name: 'mobile', color: '#4f46e5', description: 'Android/iOS' },
  { name: 'database', color: '#475569', description: 'Banco de dados e migrações' },
  { name: 'payment', color: '#16a34a', description: 'Pagamentos, TEF, cobrança' },
  { name: 'security', color: '#b91c1c', description: 'Segurança' },
  { name: 'performance', color: '#ea580c', description: 'Desempenho' },
  { name: 'documentation', color: '#64748b', description: 'Documentação' },
  { name: 'ai-generated', color: '#f97316', description: 'Criada ou executada por agente de IA' },
];

/** Normaliza URL de repositório: git@github.com:Org/Repo.git → github.com/org/repo */
export function normalizeRepoUrl(url: string): string {
  let u = url.trim().toLowerCase();
  u = u.replace(/^git\+/, '');
  u = u.replace(/^[a-z]+:\/\//, '');
  u = u.replace(/^[^@/]+@/, '');
  u = u.replace(/^([^/:]+):(?!\d)/, '$1/');
  u = u.replace(/\/+$/, '').replace(/\.git$/, '');
  return u;
}

/** História: agrupa as tarefas de desenvolvimento de uma funcionalidade (storyKey nas tarefas). */
export const STORY_TYPE: TaskType = 'story';

export const WORKLOG_SOURCES = ['manual', 'timer', 'execution'] as const;
export type WorklogSource = (typeof WORKLOG_SOURCES)[number];

/** 95 → "1h35", 30 → "30min", 0 → "0min". */
export function formatMinutes(min?: number | null): string {
  const m = Math.max(0, Math.round(min ?? 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r}min`;
  return r ? `${h}h${String(r).padStart(2, '0')}` : `${h}h`;
}

/** Aceita "1h30", "1h 30m", "90", "90m", "2h", "1.5h", "0,5h" → minutos. null se inválido. */
export function parseDuration(input: string): number | null {
  const s = input.trim().toLowerCase().replace(',', '.');
  if (!s) return null;
  if (/^\d+$/.test(s)) return Number(s);
  const dec = s.match(/^(\d+(?:\.\d+)?)\s*h$/);
  if (dec) return Math.round(Number(dec[1]) * 60);
  const m = s.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*(?:m|min)?)?$/);
  if (!m || (!m[1] && !m[2])) return null;
  return Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0);
}

/** Etapas da versão: planejada → em desenvolvimento → em testes → publicada (ou cancelada). */
export const RELEASE_STATUSES = ['planned', 'in_progress', 'testing', 'released', 'canceled'] as const;
export type ReleaseStatus = (typeof RELEASE_STATUSES)[number];
export const RELEASE_STATUS_LABELS: Record<ReleaseStatus, string> = {
  planned: 'Planejada',
  in_progress: 'Em desenvolvimento',
  testing: 'Em testes',
  released: 'Publicada',
  canceled: 'Cancelada',
};
/** Etapas em que a versão ainda recebe tarefas e pode ser publicada. */
export const OPEN_RELEASE_STATUSES: readonly ReleaseStatus[] = ['planned', 'in_progress', 'testing'];

export const RELEASE_TYPES = ['major', 'minor', 'patch', 'hotfix'] as const;
export type ReleaseType = (typeof RELEASE_TYPES)[number];
export const RELEASE_TYPE_LABELS: Record<ReleaseType, string> = {
  major: 'Major (mudança grande)',
  minor: 'Minor (novidades)',
  patch: 'Patch (correções)',
  hotfix: 'Hotfix (correção urgente)',
};

export const RELEASE_PLATFORMS = ['android', 'ios', 'web', 'api', 'desktop', 'other'] as const;
export type ReleasePlatform = (typeof RELEASE_PLATFORMS)[number];
export const RELEASE_PLATFORM_LABELS: Record<ReleasePlatform, string> = {
  android: 'Android',
  ios: 'iOS',
  web: 'Web',
  api: 'API/servidor',
  desktop: 'Desktop',
  other: 'Outra',
};

/**
 * Próximo número de versão (semver) a partir do último do componente.
 * major: 1.4.2 → 2.0.0 · minor → 1.5.0 · patch/hotfix → 1.4.3. Sem anterior: 1.0.0 (patch/hotfix: 0.0.1).
 * Prefixo "v" é mantido. Nome que não é semver: null.
 */
export function nextVersion(previous: string | null | undefined, type: ReleaseType): string | null {
  if (!previous) return type === 'major' || type === 'minor' ? '1.0.0' : '0.0.1';
  const m = previous.trim().match(/^(v?)(\d+)\.(\d+)(?:\.(\d+))?/i);
  if (!m) return null;
  const [maj, min, pat] = [Number(m[2]), Number(m[3]), Number(m[4] ?? 0)];
  const next = type === 'major' ? [maj + 1, 0, 0] : type === 'minor' ? [maj, min + 1, 0] : [maj, min, pat + 1];
  return `${m[1]}${next.join('.')}`;
}

/** Ordena números de versão (semver primeiro, do maior para o menor). */
export function compareVersions(a: string, b: string): number {
  const parse = (s: string) => s.replace(/^v/i, '').split(/[.+-]/).map((x) => (/^\d+$/.test(x) ? Number(x) : NaN));
  const [pa, pb] = [parse(a), parse(b)];
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const [x, y] = [pa[i] ?? 0, pb[i] ?? 0];
    if (Number.isNaN(x) || Number.isNaN(y)) return a.localeCompare(b);
    if (x !== y) return y - x;
  }
  return 0;
}

// ---------- Anexos ----------
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Extensões aceitas → tipo servido. SVG e HTML ficam de fora (podem executar script). */
export const ATTACHMENT_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  pdf: 'application/pdf',
  txt: 'text/plain',
  log: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  json: 'application/json',
  xml: 'application/xml',
  yaml: 'text/yaml',
  yml: 'text/yaml',
  zip: 'application/zip',
  gz: 'application/gzip',
  har: 'application/json',
  mp4: 'video/mp4',
  webm: 'video/webm',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};
export const attachmentExt = (filename: string) => (filename.includes('.') ? filename.split('.').pop()!.toLowerCase() : '');

// ---------- Menções ----------
/** Menção a pessoa/agente inserida pelo autocompletar: @[Nome](user:<id>) ou @[Nome](agent:<id>). */
export const ACTOR_MENTION_RE = /@\[([^\]\n]{1,80})\]\((user|agent):([a-f0-9]{24})\)/g;
/** Menção a tarefa: @JYN-12. */
export const TASK_MENTION_RE = new RegExp(`(?<![\\w.])@(${TASK_KEY_PREFIX}-\\d+)\\b`, 'gi');

export function parseMentions(body: string): { actors: { kind: 'user' | 'agent'; id: string; name: string }[]; taskKeys: string[] } {
  const actors = new Map<string, { kind: 'user' | 'agent'; id: string; name: string }>();
  for (const m of body.matchAll(ACTOR_MENTION_RE)) actors.set(`${m[2]}:${m[3]}`, { kind: m[2] as 'user' | 'agent', id: m[3], name: m[1] });
  const taskKeys = new Set<string>();
  for (const m of body.matchAll(TASK_MENTION_RE)) taskKeys.add(m[1].toUpperCase());
  return { actors: [...actors.values()], taskKeys: [...taskKeys] };
}

/** Markdown para exibir: menções viram links (mention:kind:id e /tasks/KEY). */
export function mentionsToMarkdown(body: string): string {
  return body
    .replace(ACTOR_MENTION_RE, (_m, name: string, kind: string, id: string) => `[@${name}](mention:${kind}:${id})`)
    .replace(TASK_MENTION_RE, (_m, key: string) => `[${key.toUpperCase()}](/tasks/${key.toUpperCase()})`);
}
